import {Injectable} from '@angular/core'

import {Observable, BehaviorSubject} from 'rxjs'
import {first, map, tap} from 'rxjs/operators'

import {SearchFilter} from '@app/core/services/search/search-filter.interface'
import {DateFilter} from '@app/core/services/date-filter/date-filter.interface'

import {
  GenericListService,
  LoadRequest,
  LoadResult,
} from '../generic-list.service'
import {DataService} from '@app/core/services/data/data.service'
import {NotificationService} from '@app/core/services/notification.service'
import {LoggerService} from '@app/core/services/logger.service'
import {ContextService} from '@app/core/services/context.service'
import {saveAs} from 'file-saver/FileSaver'

import {
  TermFilter,
  RangeFilter,
  ElasticQuery,
  MetricAggregation,
  CompositeAggregation,
} from '@app/core/services/rest-api.model'

import {HttpClient, HttpResponse} from '@angular/common/http'

export interface TransactionRequest extends LoadRequest {
  query: string
  sortField: string
  sortAsc: boolean
}

export interface Totals {
  currency: string
  any?: any
}

export interface ExportPayload {
  options: {
    includeTxns: boolean
    excludeDeclined: boolean
    allRowsData: boolean
    extraTxnInfo: boolean
  }
  maxCount: number
  timezone: string
  queryParams: {
    filter: {
      query: string
    }
    range: DateFilter
  }
}

const MAX_BUCKETS = 10000

@Injectable()
export class TransactionListService extends GenericListService<object> {
  filter: SearchFilter = {
    query: '-disposition:FAILED -state:CARD',
    asc: false,
    sort: '@timestamp',
  }
  range: DateFilter = null
  index = 'Transactions'

  approved = new BehaviorSubject('0')
  declined = new BehaviorSubject('0')
  totals = new BehaviorSubject([])
  totalTransactions$ = new BehaviorSubject(0)

  constructor(
    private dataSvc: DataService,
    private http: HttpClient,
    protected notify: NotificationService,
    protected contextSvc: ContextService,
    protected logger: LoggerService,
  ) {
    super(notify, logger)
  }

  getApproved(): BehaviorSubject<string> {
    return this.approved
  }

  getDeclined(): BehaviorSubject<string> {
    return this.declined
  }

  getTotals(): BehaviorSubject<Totals[]> {
    return this.totals
  }

  getTotalTransactions(): Observable<number> {
    return this.totalTransactions$.asObservable()
  }

  retrieve(request: LoadRequest): Observable<LoadResult<object>> {
    this.totalTransactions$.next(0) // reset total count
    const elastic = this.createElasticQuery(
      request.domain,
      request.offset,
      request.count,
    )
    return this.dataSvc.getData(elastic).pipe(
      map((res) => {
        const result: LoadResult<object> = {
          total: res.count,
          items: res.documents,
        }
        const myTotals: Totals[] = []
        if (res.aggregations.totals !== undefined) {
          const currencies = new Map<string, Totals>()
          res.aggregations.totals.buckets.forEach((element) => {
            if (element.key !== undefined) {
              const currentTotalTransactions = this.totalTransactions$.value
              this.totalTransactions$.next(
                currentTotalTransactions + element.count,
              )
              const currency = element.key.currency
              if (currencies.get(currency) == null) {
                currencies.set(currency, {currency: currency})
              }
              const obj = currencies.get(currency)

              const disposition = element.key.disposition.toLowerCase()
              const state = element.key.state.toLowerCase()

              obj[disposition + '_count'] =
                obj[disposition + '_count'] != null
                  ? obj[disposition + '_count'] + element.count
                  : element.count
              obj[disposition + '_' + state + '_count'] = element.count
              obj[disposition + '_' + state + '_value'] =
                element.aggregations.total.value
            }
          })
          myTotals.push(...currencies.values())
        }

        this.totals.next(myTotals)
        return result
      }),
    )
  }

  private createElasticQuery(
    domain: string,
    offset: number,
    count: number,
  ): ElasticQuery {
    const elastic = new ElasticQuery(this.index, '', count)
    const compositeAggregation = new CompositeAggregation(
      'totals',
      [{field: 'currency'}, {field: 'disposition'}, {field: 'state'}],
      MAX_BUCKETS,
    )
    compositeAggregation.addAggregation(
      new MetricAggregation('total', 'committedAmount', 'sum'),
    )
    compositeAggregation.addAggregation(
      new MetricAggregation('authorized', 'authorizedAmount', 'sum'),
    )
    elastic.addAggregation(compositeAggregation)

    elastic.offset = offset
    if (this.range) {
      elastic.addFilter(new RangeFilter(this.range.start, this.range.end))
    }
    elastic.addFilter(new TermFilter('siteAccessDomain', domain))

    if (this.filter) {
      elastic.query = this.filter.query
      if (this.filter.sort) {
        const field =
          this.filter.sort === 'time' ? '@timestamp' : this.filter.sort
        elastic.sorts = [{field: field, asc: this.filter.asc}]
      } else {
        // default to time
        elastic.sorts = [{field: '@timestamp', asc: this.filter.asc}]
      }
    }
    return elastic
  }

  private getFormattedTimestamp(): string {
    const now = new Date()
    return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`
  }

  exportTransactions(domain: string, request: ExportPayload): Observable<any> {
    const url = `api://Reports/${domain}/Transactions`
    const options: {
      responseType: 'arraybuffer'
      observe: 'response'
    } = {
      responseType: 'arraybuffer',
      observe: 'response',
    }

    return this.http.post(url, request, options).pipe(
      first(),
      tap((r: HttpResponse<any>) => {
        const timestamp = this.getFormattedTimestamp()
        saveAs(
          new Blob([r.body], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;',
          }),
          `Transactions_${timestamp}`,
        )
      }),
    )
  }
}
